import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button } from '@advanza/ui'
import { useToast } from 'components/misc/Toaster'
import { FreeLeadTypeOption } from 'interfaces/FreeLeads'
import { hasAccess } from 'misc/user'
import { useEffect, useState } from 'react'

interface EditableFields {
    label: string
    requires_comment: boolean
    active: boolean
}

interface NewTypeFields extends EditableFields {
    value: string
}

const emptyNewType: NewTypeFields = {
    value: '',
    label: '',
    requires_comment: false,
    active: true,
}

const cellStyle: React.CSSProperties = { padding: '8px', textAlign: 'left' }

const FreeLeadTypesList = () => {
    const { addToast } = useToast()
    const hasWriteAccess = hasAccess('settings', 'w')
    const [items, setItems] = useState<FreeLeadTypeOption[]>([])
    const [loading, setLoading] = useState(true)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editFields, setEditFields] = useState<EditableFields | null>(null)
    const [newType, setNewType] = useState<NewTypeFields>(emptyNewType)
    const [isSaving, setIsSaving] = useState(false)

    const fetchItems = async () => {
        setLoading(true)
        try {
            const response = await call('office/free-lead-types/get-list')
            setItems(response.items ?? [])
        } catch {
            addToast({ msg: 'Failed to load free lead types.', red: true })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchItems()
    }, [])

    const startEdit = (item: FreeLeadTypeOption) => {
        setEditingId(item.free_lead_type_id)
        setEditFields({
            label: item.label,
            requires_comment: item.requires_comment,
            active: item.active,
        })
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditFields(null)
    }

    const saveEdit = async () => {
        if (editingId === null || editFields === null || !editFields.label.trim()) {
            return
        }
        setIsSaving(true)
        try {
            await call(`office/free-lead-types/save/${editingId}`, {
                method: 'POST',
                json: editFields,
            })
            addToast({ msg: 'Saved.', green: true })
            cancelEdit()
            await fetchItems()
        } catch {
            addToast({ msg: 'Failed to save.', red: true })
        } finally {
            setIsSaving(false)
        }
    }

    const saveNewType = async () => {
        setIsSaving(true)
        try {
            await call('office/free-lead-types/save', {
                method: 'POST',
                json: newType,
            })
            addToast({ msg: 'Type added.', green: true })
            setNewType(emptyNewType)
            await fetchItems()
        } catch (response) {
            const fieldErrors =
                response && typeof response === 'object'
                    ? Object.values(
                          (response as { free_lead_type?: Record<string, string> })
                              .free_lead_type ?? {},
                      )
                    : []
            addToast({
                msg: fieldErrors[0] ?? 'Failed to add type. Is the value unique and lowercase?',
                red: true,
            })
        } finally {
            setIsSaving(false)
        }
    }

    const deactivate = async (item: FreeLeadTypeOption) => {
        if (!window.confirm(`Deactivate "${item.label}"? It disappears from the giveaway modal.`)) {
            return
        }
        try {
            await call(`office/free-lead-types/delete/${item.free_lead_type_id}`, {
                method: 'POST',
            })
            addToast({ msg: 'Type deactivated.', green: true })
            await fetchItems()
        } catch {
            addToast({ msg: 'Failed to deactivate.', red: true })
        }
    }

    if (loading) {
        return <div>Loading...</div>
    }

    const isValidNewValue = /^[a-z0-9_]+$/.test(newType.value)
    const canAdd = !isSaving && isValidNewValue && !!newType.label.trim()

    return (
        <div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #dddddd' }}>
                        <th style={cellStyle}>Label</th>
                        <th style={cellStyle}>Value</th>
                        <th style={cellStyle}>Source</th>
                        <th style={cellStyle}>Requires comment</th>
                        <th style={cellStyle}>Active</th>
                        {hasWriteAccess && <th style={cellStyle}>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {items.map((item) => {
                        const isEditing = editingId === item.free_lead_type_id
                        return (
                            <tr
                                key={item.free_lead_type_id}
                                style={{
                                    borderBottom: '1px solid #eeeeee',
                                    opacity: item.active ? 1 : 0.5,
                                }}>
                                <td style={cellStyle}>
                                    {isEditing && editFields ? (
                                        <InputField
                                            value={editFields.label}
                                            onChange={(e) =>
                                                setEditFields({
                                                    ...editFields,
                                                    label: e.target.value,
                                                })
                                            }
                                        />
                                    ) : (
                                        item.label
                                    )}
                                </td>
                                <td style={cellStyle}>
                                    <code>{item.value}</code>
                                </td>
                                <td style={cellStyle}>{item.is_manual ? 'Manual' : 'Automated'}</td>
                                <td style={cellStyle}>
                                    {isEditing && editFields ? (
                                        <input
                                            type="checkbox"
                                            checked={editFields.requires_comment}
                                            onChange={(e) =>
                                                setEditFields({
                                                    ...editFields,
                                                    requires_comment: e.target.checked,
                                                })
                                            }
                                        />
                                    ) : item.requires_comment ? (
                                        'Yes'
                                    ) : (
                                        'No'
                                    )}
                                </td>
                                <td style={cellStyle}>
                                    {isEditing && editFields ? (
                                        <input
                                            type="checkbox"
                                            checked={editFields.active}
                                            onChange={(e) =>
                                                setEditFields({
                                                    ...editFields,
                                                    active: e.target.checked,
                                                })
                                            }
                                        />
                                    ) : item.active ? (
                                        'Yes'
                                    ) : (
                                        'No'
                                    )}
                                </td>
                                {hasWriteAccess && (
                                    <td style={cellStyle}>
                                        {item.is_manual &&
                                            (isEditing ? (
                                                <>
                                                    <Button disabled={isSaving} onClick={saveEdit}>
                                                        Save
                                                    </Button>{' '}
                                                    <Button name="link" onClick={cancelEdit}>
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        name="text"
                                                        onClick={() => startEdit(item)}>
                                                        Edit
                                                    </Button>{' '}
                                                    {item.active && (
                                                        <Button
                                                            name="text"
                                                            onClick={() => deactivate(item)}>
                                                            Deactivate
                                                        </Button>
                                                    )}
                                                </>
                                            ))}
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
            {hasWriteAccess && (
                <div
                    style={{
                        marginTop: '24px',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                    }}>
                    <InputField
                        legend
                        placeholder="Value (e.g. summer_campaign_2026)"
                        value={newType.value}
                        onChange={(e) =>
                            setNewType({
                                ...newType,
                                value: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                            })
                        }
                    />
                    <InputField
                        legend
                        placeholder="Label"
                        value={newType.label}
                        onChange={(e) => setNewType({ ...newType, label: e.target.value })}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <input
                            type="checkbox"
                            checked={newType.requires_comment}
                            onChange={(e) =>
                                setNewType({ ...newType, requires_comment: e.target.checked })
                            }
                        />
                        Requires comment
                    </label>
                    <Button disabled={!canAdd} onClick={saveNewType}>
                        Add manual type
                    </Button>
                </div>
            )}
        </div>
    )
}

export default FreeLeadTypesList
